import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Play, CheckCircle2, ScanLine, Timer, Trophy, History } from "lucide-react";
import { toast } from "sonner";

const fmtDur = (s) => {
  if (!s || s < 0) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), r = s % 60;
  return `${m}m ${r}s`;
};

export default function ShelvesTour() {
  const [zones, setZones] = useState([]);
  const [runs, setRuns] = useState([]);
  const [active, setActive] = useState(null);
  const [startDlg, setStartDlg] = useState(false);
  const [scanInput, setScanInput] = useState("");

  const load = async () => {
    const [z, r] = await Promise.all([
      api.get("/shelves/zones"),
      api.get("/shelves/tour-runs?limit=10"),
    ]);
    setZones(z.data); setRuns(r.data);
  };
  useEffect(() => { load(); }, []);

  const startRun = async (zone_id) => {
    try {
      const { data } = await api.post("/shelves/tour-runs", { zone_id: zone_id || null });
      setActive(data);
      setStartDlg(false);
      toast.success(`Rundgang gestartet · ${data.stops.length} Stationen`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const scan = async (shelfCode) => {
    if (!active) return;
    try {
      const { data } = await api.post(`/shelves/tour-runs/${active.id}/scan`,
                                        { shelf_code: shelfCode });
      setActive(data);
      setScanInput("");
      if (data.completed_at) {
        toast.success("Rundgang abgeschlossen!");
        load();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Scan-Fehler"); }
  };

  const finish = async () => {
    if (!active) return;
    try {
      await api.post(`/shelves/tour-runs/${active.id}/complete`);
      toast.success("Rundgang manuell abgeschlossen");
      const { data } = await api.get(`/shelves/tour-runs/${active.id}`);
      setActive(data); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  return (
    <div data-testid="page-shelves-tour">
      <PageHeader
        eyebrow="Warehouse"
        title="Regal-Rundgang"
        description="Kommissionier-Rundgänge live durchführen, per Scan abhaken und Zeit pro Regal messen."
        actions={
          <Button onClick={() => setStartDlg(true)} disabled={!!active && !active.completed_at}
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"
                  data-testid="btn-start-tour">
            <Play size={14} className="mr-1" /> Neuen Rundgang starten
          </Button>
        }
      />

      {active && <ActiveTour active={active} scan={scan} finish={finish}
                              scanInput={scanInput} setScanInput={setScanInput}
                              onClose={() => setActive(null)} />}

      {!active && (
        <Card className="p-6 rounded-2xl mb-6 text-center bg-[var(--pop-cream)]/40" data-testid="tour-idle">
          <ScanLine size={40} className="mx-auto mb-3 text-[var(--pop-pink)]" />
          <div className="font-display font-black text-xl">Kein aktiver Rundgang</div>
          <div className="text-sm text-[var(--text-muted)] mt-1">Starte einen neuen Rundgang, um Regale in Wanderreihenfolge abzuhaken.</div>
        </Card>
      )}

      {/* Historie */}
      <div className="mt-6" data-testid="tour-history">
        <div className="flex items-center gap-2 mb-3">
          <History size={16} /><h2 className="font-display font-bold text-lg">Vergangene Rundgänge</h2>
        </div>
        <Card className="p-0 overflow-hidden rounded-2xl">
          <table className="w-full text-sm">
            <thead className="bg-[var(--pop-cream)]/40">
              <tr>
                <th className="text-left px-4 py-2 overline">Label</th>
                <th className="text-left px-4 py-2 overline">Start</th>
                <th className="text-left px-4 py-2 overline">Fortschritt</th>
                <th className="text-right px-4 py-2 overline">Gesamt</th>
                <th className="text-right px-4 py-2 overline">Ø / Stopp</th>
                <th className="text-right px-4 py-2 overline">Langsamster</th>
                <th className="text-left px-4 py-2 overline">Läufer</th>
                <th className="text-right px-4 py-2 overline">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-t border-[var(--border)]" data-testid={`tour-row-${r.id}`}>
                  <td className="px-4 py-2 font-medium">{r.label}</td>
                  <td className="px-4 py-2 font-mono text-xs">{(r.started_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-2">
                    <span className={`pop-pill text-[10px] font-bold ${r.completed_at ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                      {r.progress.done}/{r.progress.total} ({r.progress.pct}%)
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{fmtDur(r.timing.total_seconds)}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmtDur(r.timing.avg_per_stop)}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmtDur(r.timing.slowest_seconds)}</td>
                  <td className="px-4 py-2 text-xs">{r.user_name}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={async () => {
                      const { data } = await api.get(`/shelves/tour-runs/${r.id}`);
                      setActive(data);
                    }}
                            data-testid={`btn-open-tour-${r.id}`}
                            className="text-xs font-semibold hover:underline">Details</button>
                  </td>
                </tr>
              ))}
              {runs.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-[var(--text-muted)]">Noch keine Rundgänge.</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Start-Dialog */}
      <Dialog open={startDlg} onOpenChange={setStartDlg}>
        <DialogContent className="max-w-md" data-testid="dialog-start-tour">
          <DialogHeader><DialogTitle className="font-display text-2xl">Neuen Rundgang starten</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Zone (optional)</Label>
              <div className="flex flex-col gap-2 mt-2">
                <Button onClick={() => startRun(null)} variant="outline"
                        className="rounded-full justify-start"
                        data-testid="btn-start-all">
                  Alle Zonen ({zones.length})
                </Button>
                {zones.map((z) => (
                  <Button key={z.id} onClick={() => startRun(z.id)} variant="outline"
                          className="rounded-full justify-start"
                          data-testid={`btn-start-zone-${z.id}`}>
                    <span className="w-3 h-3 rounded mr-2" style={{ background: z.color || "#eb75b5" }} />
                    {z.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActiveTour({ active, scan, finish, scanInput, setScanInput, onClose }) {
  const stops = active.stops || [];
  const nextStop = useMemo(() => stops.find((s) => !s.scanned_at), [stops]);
  const done = stops.filter((s) => s.scanned_at).length;
  const totalDone = stops.filter((s) => s.duration_seconds != null)
                          .reduce((n, s) => n + (s.duration_seconds || 0), 0);
  const avg = done ? Math.round(totalDone / done) : 0;

  const onSubmit = (e) => {
    e.preventDefault();
    const code = scanInput.trim();
    if (code) scan(code);
  };

  return (
    <Card className="p-4 rounded-2xl mb-6 border-2 border-[var(--pop-pink)]/40" data-testid="active-tour">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="font-display font-black text-xl flex items-center gap-2">
            <Trophy size={20} className="text-[var(--pop-yellow-dark)]" /> {active.label}
          </div>
          <div className="text-xs text-[var(--text-muted)] font-mono">Läuft seit {(active.started_at || "").slice(11, 19)} · Läufer: {active.user_name}</div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="text-xs text-[var(--text-muted)]">Fortschritt</div>
            <div className="font-display text-2xl font-black">{done}/{stops.length}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-[var(--text-muted)]">Ø / Stopp</div>
            <div className="font-display text-2xl font-black flex items-center gap-1"><Timer size={16}/>{fmtDur(avg)}</div>
          </div>
        </div>
        {active.completed_at ? (
          <Button variant="outline" onClick={onClose} className="rounded-full">Schließen</Button>
        ) : (
          <Button variant="outline" onClick={finish} className="rounded-full" data-testid="btn-finish-tour">
            <CheckCircle2 size={14} className="mr-1" /> Manuell abschließen
          </Button>
        )}
      </div>

      {!active.completed_at && nextStop && (
        <form onSubmit={onSubmit} className="mb-4 flex items-center gap-2 bg-[var(--pop-cream)]/40 rounded-xl p-3">
          <ScanLine size={18} className="text-[var(--pop-pink)]" />
          <div className="text-sm">
            Nächstes Regal: <b className="font-mono">{nextStop.shelf_code}</b>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Input value={scanInput} onChange={(e) => setScanInput(e.target.value)}
                   placeholder="Regal-Code scannen / eingeben"
                   className="w-56 font-mono"
                   autoFocus
                   data-testid="input-tour-scan" />
            <Button type="submit"
                    className="rounded-full bg-[var(--pop-pink)] text-white hover:opacity-90"
                    data-testid="btn-tour-scan">
              Abhaken
            </Button>
            <Button type="button" variant="outline" onClick={() => scan(nextStop.shelf_code)}
                    className="rounded-full text-xs"
                    data-testid="btn-tour-skip-scan">
              Nächstes ohne Scan
            </Button>
          </div>
        </form>
      )}

      <div className="space-y-1 max-h-96 overflow-y-auto">
        {stops.map((s, i) => (
          <div key={s.shelf_code} data-testid={`tour-stop-${s.shelf_code}`}
                className={`flex items-center gap-3 p-2 rounded-lg text-sm ${
                  s.scanned_at ? "bg-green-50 text-green-900" :
                  nextStop && nextStop.shelf_code === s.shelf_code ? "bg-yellow-50 border border-yellow-300" :
                  "bg-white"
                }`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-mono font-bold text-xs ${s.scanned_at ? "bg-green-500 text-white" : "bg-gray-200 text-gray-500"}`}>
              {s.scanned_at ? "✓" : i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono font-bold">{s.shelf_code}</div>
              {s.shelf_name && s.shelf_name !== s.shelf_code &&
                <div className="text-xs text-[var(--text-muted)] truncate">{s.shelf_name}</div>}
            </div>
            {s.duration_seconds != null && (
              <div className="text-xs font-mono text-[var(--text-muted)]">
                <Timer size={10} className="inline mr-1" /> {fmtDur(s.duration_seconds)}
              </div>
            )}
            {s.scanned_at && (
              <div className="text-[10px] font-mono text-[var(--text-muted)]">
                {(s.scanned_at || "").slice(11, 19)}
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
